import React, { useContext, useEffect, useRef } from 'react';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { ApiContext } from '@api/api';
import { CurrencyLockTypeEnum, ExpeditionStatusEnum, FeaturesEnum, ResponseDeadlineModeEnum } from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import ExpeditionStateSpan from '@components/obsolete/StateSpan/ExpeditionStateSpan.tsx';
import SvgRoute from '@icons/route.svg?react';
import CarrierDistributionSelector, {
  CarrierDistributionSelectorData,
  DistributionType,
} from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import { splitBulkDistributionSelection } from '@app/pages/customer/expedition-list/bulkDistributionEligibility.ts';
import { ladenFactorValueValidation } from '@utils/expeditionUtils.ts';

/** A selected shipment as the mass-action sidebar has it. */
export interface BulkDistributionExpedition {
  id: string;
  applicationId?: string;
  expeditionStatus?: ExpeditionStatusEnum;
  isManualExpedition?: boolean;
}

interface ComponentProps {
  expeditions: BulkDistributionExpedition[];
  distribution: CarrierDistributionSelectorData;
  onChange: (distribution: CarrierDistributionSelectorData) => void;
  /** Validation errors with their full form path (e.g. `distribution.price.price`). */
  validationErrors: { path: string; message: string }[];
}

type TranslateFn = (key: string, fallback: string, data?: object) => string;

/** Yup shape for the `distribution` object — same rules as RequestCarrierSidebar / AssignCarrierSidebar, laden factor as a free whole number. */
export const bulkDistributionValidationShape = (tr: TranslateFn, ladenFactorFeatureOn: boolean) => ({
  distributionType: yup.string(),
  ladenFactorValue: ladenFactorValueValidation(
    {
      shouldBeNumber: tr('RequestCarrierSidebar.ladenFactorShouldBeNumber', 'Laden factor should be a number'),
      required: tr('RequestCarrierSidebar.mandatoryLadenFactor', 'Laden factor is required'),
    },
    ladenFactorFeatureOn,
    type => type === 'SPOT' || type === 'FIXED',
  )
    .integer(tr('BulkDistributionForm.ladenFactorInteger', 'Ložný faktor musí být celé číslo'))
    .min(1, tr('BulkDistributionForm.ladenFactorMin', 'Ložný faktor musí být alespoň 1')),
  price: yup.object().when('distributionType', {
    is: (type: string) => !!type,
    then: yup
      .object()
      .shape({
        price: yup
          .number()
          .typeError(tr('RequestCarrierSidebar.priceShouldBeNumber', 'Price should be number'))
          .transform(value => (isNaN(value) ? undefined : value))
          .nullable()
          .required(tr('RequestCarrierSidebar.mandatoryPrice', 'Price is required')),
        currency: yup.string().required(tr('CreateExpedition.mandatoryCurrency', 'Currency is required')),
      })
      .required(tr('RequestCarrierSidebar.mandatoryPrice', 'Price is required')),
    otherwise: yup.object().nullable(),
  }),
  selectedItems: yup.array().when('distributionType', {
    is: (type: string) => type === 'FIXED' || type === 'SPOT' || type === 'ASSIGN',
    then: yup
      .array()
      .min(1, tr('RequestCarrierSidebar.mandatoryCarriers', 'Please select at least one carrier'))
      .required(tr('RequestCarrierSidebar.mandatoryCarriers', 'Please select at least one carrier')),
    otherwise: yup.array().nullable(),
  }),
  responseDeadlineOffsetValue: yup.number().when(['distributionType', 'responseDeadlineMode'], {
    is: (type: string, mode: string) => (type === 'FIXED' || type === 'SPOT') && mode !== 'absolute',
    then: yup
      .number()
      .typeError(tr('InputDeadline.required', 'Offer deadline is required'))
      .min(1, tr('InputDeadline.required', 'Offer deadline is required'))
      .required(tr('InputDeadline.required', 'Offer deadline is required')),
    otherwise: yup.number().nullable(),
  }),
  responseDeadlineLocalTime: yup.mixed().when(['distributionType', 'responseDeadlineMode'], {
    is: (type: string, mode: string) => (type === 'FIXED' || type === 'SPOT') && mode === 'absolute',
    then: yup.mixed().required(tr('InputDeadline.required', 'Offer deadline is required')),
    otherwise: yup.mixed().nullable(),
  }),
});

export const initBulkDistribution = (
  loggedUser: { defaultTimezoneId?: string; defaultCurrencyIsoCode?: string },
  currencyLockEnabled: boolean,
): CarrierDistributionSelectorData => ({
  distributionType: DistributionType.SPOT,
  selectedItems: [],
  responseDeadlineMode: 'relative',
  responseDeadlineOffsetValue: 8,
  responseDeadlineOffsetUnit: 'hours',
  responseDeadlineTimezone: loggedUser?.defaultTimezoneId,
  price: { currency: loggedUser?.defaultCurrencyIsoCode, price: 0 },
  notifyAssignedCarrier: false,
  currencyLockType: currencyLockEnabled ? CurrencyLockTypeEnum.ONLY_SELECTED : CurrencyLockTypeEnum.NONE,
  ladenFactor: false,
  ladenFactorValue: null,
  allowPartialDelivery: false,
});

const divider = <div className={'h-px w-full bg-content-quaternary'} />;

/**
 * Bulk carrier tendering (RD-2922): the same carrier-distribution form the single "Quote carrier" /
 * "Assign carrier" sidebars use, applied to every selected NEW expedition by an async job. Rows in
 * another state are listed and skipped; the job then validates every row (state, active distribution,
 * laden factor vs handling units) and rests in DRAFT until the user removed every problem row.
 *
 * Deliberately narrower than the per-expedition form: no API carriers (their route validation and
 * DHL pickup timezone are per expedition), no AI auto-selection and no "load original distribution".
 * The laden factor is a free whole number here.
 */
const BulkDistributionForm: React.FC<ComponentProps> = ({ expeditions, distribution, onChange, validationErrors }) => {
  const { tr } = useTranslationLgs();
  const { getDistributionSettings } = useContext(ApiContext);
  const { eligible, skipped } = splitBulkDistributionSelection(expeditions);

  // The company default deadline arrives after mount; patch the latest value, not the one closed over.
  const latest = useRef(distribution);
  latest.current = distribution;
  useEffect(() => {
    getDistributionSettings(d => {
      const hours = d.defaultHoursInFuture;
      const mode: 'relative' | 'absolute' = d.responseDeadlineMode === ResponseDeadlineModeEnum.ABSOLUTE ? 'absolute' : 'relative';
      onChange({
        ...latest.current,
        responseDeadlineOffsetValue: hours,
        responseDeadlineMode: mode,
        responseDeadlineLocalTime: mode === 'absolute' ? dayjs().add(hours, 'hour') : latest.current.responseDeadlineLocalTime,
      });
    });
  }, []);

  return (
    <div className={'flex flex-col gap-6'}>
      <div className={'flex flex-col gap-4'}>
        <div className={'flex flex-row items-start gap-4'}>
          <SvgRoute className={'mt-1 h-4 w-4 shrink-0 text-content-tertiary'} />
          <div className={'flex flex-col gap-2'}>
            <span className={'text-sm font-medium leading-3 text-content-secondary'}>
              {tr('BulkDistributionForm.forExpeditions', 'Pro přepravy ({{count}})', { count: eligible.length })}
            </span>
            <span className={'font-bold text-content-primary'} data-testid={'bulk-distribution-eligible'}>
              {eligible.map(e => e.applicationId ?? e.id).join(', ')}
            </span>
          </div>
        </div>
        {divider}
      </div>

      {skipped.length > 0 && (
        <COInlineMessage variant={'warning'} dataTestId={'bulk-distribution-skipped'}>
          <div className={'flex flex-col gap-2'}>
            <span>{tr('BulkDistributionForm.skippedWarning', 'Tyto přepravy nejsou ve stavu Nová a budou přeskočeny:')}</span>
            <ul className={'flex flex-col gap-1 pl-4 list-disc'}>
              {skipped.map(row => (
                <li key={row.id} className={'flex flex-row items-center gap-2'}>
                  <span className={'font-medium'}>{row.applicationId ?? row.id}</span>
                  {row.expeditionStatus != null && <ExpeditionStateSpan expeditionStage={row.expeditionStatus} isManual={row.isManualExpedition ?? false} />}
                </li>
              ))}
            </ul>
          </div>
        </COInlineMessage>
      )}

      {eligible.length === 0 && (
        <COInlineMessage variant={'error'} dataTestId={'bulk-distribution-none-eligible'}>
          {tr('BulkDistributionForm.noneEligible', 'Žádná z vybraných přeprav není ve stavu Nová.')}
        </COInlineMessage>
      )}

      <CarrierDistributionSelector
        allowedDistributionTypes={[DistributionType.SPOT, DistributionType.FIXED, DistributionType.ASSIGN]}
        dataTestIdPrefix={'bulkDistribution'}
        distribution={distribution}
        validationErrors={validationErrors}
        onChange={onChange}
        ladenFactorEligible
        ladenFactorFreeInput
        excludeApiCarriers
        hideAiAutoSelection
      />
    </div>
  );
};

export default BulkDistributionForm;
